import React, { useEffect, useState } from 'react';
import { moodboardService } from '../lib/moodboardService';
import { RoomStyle } from '../types/moodboard';

const RoomStyleManager: React.FC = () => {
  const [styles, setStyles] = useState<RoomStyle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newStyleName, setNewStyleName] = useState('');
  const [newStyleDescription, setNewStyleDescription] = useState('');
  const [editingStyle, setEditingStyle] = useState<RoomStyle | null>(null);

  const fetchStyles = async () => {
    try {
      setLoading(true);
      const styleList = await moodboardService.getRoomStyles();
      setStyles(styleList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStyles();
  }, []);

  const handleAddStyle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStyleName.trim()) {
      return;
    }

    try {
      await moodboardService.addRoomStyle(newStyleName, newStyleDescription);
      setNewStyleName('');
      setNewStyleDescription('');
      fetchStyles(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };

  const handleUpdateStyle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStyle) return;

    try {
      await moodboardService.updateRoomStyle(editingStyle.id, editingStyle.name, editingStyle.description || '');
      setEditingStyle(null);
      fetchStyles(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };

  if (loading) return <p>Loading styles...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <div className="bg-white shadow rounded-lg p-4 mt-6">
      <h3 className="text-lg font-semibold mb-2">Room Styles</h3>
      <form onSubmit={handleAddStyle} className="space-y-2 mb-4">
        <input
          type="text"
          value={newStyleName}
          onChange={(e) => setNewStyleName(e.target.value)}
          placeholder="New style name"
          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
        />
        <input
          type="text"
          value={newStyleDescription}
          onChange={(e) => setNewStyleDescription(e.target.value)}
          placeholder="Description"
          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
        />
        <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          Add
        </button>
      </form>
      <ul className="divide-y divide-gray-200">
        {styles.map((style) => (
          <li key={style.id} className="py-2">
            {editingStyle && editingStyle.id === style.id ? (
              <form onSubmit={handleUpdateStyle} className="space-y-2">
                <input
                  type="text"
                  value={editingStyle.name}
                  onChange={(e) => setEditingStyle({ ...editingStyle, name: e.target.value })}
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
                <input
                  type="text"
                  value={editingStyle.description}
                  onChange={(e) => setEditingStyle({ ...editingStyle, description: e.target.value })}
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
                <div className="flex space-x-2">
                  <button type="submit" className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700">Save</button>
                  <button type="button" onClick={() => setEditingStyle(null)} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
                </div>
              </form>
            ) : (
              <div className="flex justify-between items-center">
                <div>
                  <strong>{style.name}:</strong> {style.description}
                </div>
                <button onClick={() => setEditingStyle(style)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700">Edit</button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RoomStyleManager; 